import React from "react";
import PageHeader from "../components/PageHeader";
import { Badge } from "@/components/ui/badge";

const Logistics = () => {
  return (
    <div className="bg-dreibach-black min-h-screen pb-24">
      <PageHeader 
        title="Infrastructure" 
        subtitle="50 Orders Per Minute. Zero Friction."
        image="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=2000"
      />

      <div className="container mx-auto px-6 py-24">
        
        {/* Intro */}
        <div className="max-w-4xl mx-auto text-center mb-24">
          <h2 className="heading-md text-white mb-8">The Backbone of Commerce</h2>
          <p className="body-lg text-gray-300">
             Logistics is our core product. We have built a proprietary supply chain network that spans from global manufacturing hubs to our customer's doorstep.
          </p>
        </div>

        {/* Map Visualization */}
        <div className="relative w-full h-[600px] bg-dreibach-navy/30 border border-white/10 rounded-lg overflow-hidden mb-24 group">
            <div className="absolute inset-0 opacity-20 bg-[url('https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Outline_Map_of_United_Kingdom.svg/2000px-Outline_Map_of_United_Kingdom.svg.png')] bg-contain bg-center bg-no-repeat"></div>
            
            {/* Map Points */}
            
            {/* Borehamwood HQ */}
            <div className="absolute top-[75%] left-[60%] flex flex-col items-center group/point hover:z-50">
                <div className="w-4 h-4 bg-white rounded-full relative z-10 shadow-[0_0_20px_rgba(255,255,255,0.8)]">
                    <div className="absolute inset-0 rounded-full bg-white animate-ping opacity-50"></div>
                </div>
                <div className="mt-4 bg-dreibach-black border border-white/20 p-4 w-64 text-center opacity-0 group-hover/point:opacity-100 transition-opacity absolute top-4 pointer-events-none">
                    <h4 className="text-dreibach-teal font-bold uppercase tracking-wider mb-1">Borehamwood (HQ)</h4>
                    <p className="text-gray-400 text-xs">The Command Centre.<br/>Established 2021.</p>
                </div>
            </div>

            {/* Wales */}
            <div className="absolute top-[65%] left-[40%] flex flex-col items-center group/point hover:z-50">
                 <div className="w-4 h-4 bg-dreibach-teal rounded-full relative z-10"></div>
                 <div className="mt-4 bg-dreibach-black border border-white/20 p-4 w-64 text-center opacity-0 group-hover/point:opacity-100 transition-opacity absolute top-4 pointer-events-none">
                    <h4 className="text-dreibach-teal font-bold uppercase tracking-wider mb-1">Wales Facility</h4>
                    <p className="text-gray-400 text-xs">90,000 sq ft.<br/>Bulk Inventory & Overflow.</p>
                </div>
            </div>

             {/* Leicester */}
             <div className="absolute top-[60%] left-[60%] flex flex-col items-center group/point hover:z-50">
                 <div className="w-4 h-4 bg-dreibach-teal rounded-full relative z-10"></div>
                 <div className="mt-4 bg-dreibach-black border border-white/20 p-4 w-64 text-center opacity-0 group-hover/point:opacity-100 transition-opacity absolute top-4 pointer-events-none">
                    <h4 className="text-dreibach-teal font-bold uppercase tracking-wider mb-1">Leicester Hub</h4>
                    <p className="text-gray-400 text-xs">100,000 sq ft.<br/>Network Optimisation.</p>
                </div>
            </div>

            {/* Northampton */}
            <div className="absolute top-[65%] left-[62%] flex flex-col items-center group/point hover:z-50">
                 <div className="w-6 h-6 border-2 border-dreibach-teal bg-dreibach-black rounded-full relative z-10 flex items-center justify-center">
                    <div className="w-2 h-2 bg-dreibach-teal rounded-full"></div>
                 </div>
                 <div className="mt-4 bg-dreibach-black border border-dreibach-teal p-4 w-64 text-center absolute top-6 z-20">
                    <h4 className="text-dreibach-teal font-bold uppercase tracking-wider mb-1">Northampton Super-Hub</h4>
                    <p className="text-gray-400 text-xs">40,000 sq ft.<br/>High-Throughput Processing.<br/>Opened 2025.</p>
                </div>
            </div>
            
            <div className="absolute bottom-8 left-8 bg-black/80 backdrop-blur p-6 border border-white/10">
                <h3 className="text-white uppercase font-bold tracking-widest mb-4 text-sm">Network Map</h3>
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                        <span className="text-gray-400 text-xs uppercase">Headquarters</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 border-2 border-dreibach-teal bg-black rounded-full"></div>
                        <span className="text-gray-400 text-xs uppercase">Super-Hub</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-dreibach-teal rounded-full"></div>
                        <span className="text-gray-400 text-xs uppercase">Distribution Centre</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-dreibach-card border border-white/10 p-10 hover:border-dreibach-teal/50 transition-colors">
                <div className="text-dreibach-teal font-black text-4xl mb-2">2 PM</div>
                <h4 className="text-white font-bold uppercase tracking-wide mb-4">Velocity</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                    Same-Day Dispatch for all orders placed by 2 PM. Our automated sorting systems ensure zero delay.
                </p>
            </div>
            
            <div className="bg-dreibach-card border border-white/10 p-10 hover:border-dreibach-teal/50 transition-colors">
                <div className="text-dreibach-teal font-black text-4xl mb-2">500</div>
                <h4 className="text-white font-bold uppercase tracking-wide mb-4">Volume</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                    Processing 500 containers per year. Our import capabilities rival specialized freight forwarders.
                </p>
            </div>

            <div className="bg-dreibach-card border border-white/10 p-10 hover:border-dreibach-teal/50 transition-colors">
                <div className="text-dreibach-teal font-black text-4xl mb-2">SYNC</div>
                <h4 className="text-white font-bold uppercase tracking-wide mb-4">Technology</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                    Real-time Multi-Channel synchronization across Amazon, eBay, and DTC platforms.
                </p>
            </div>
        </div>

      </div>
    </div>
  );
};

export default Logistics;
